import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { useRegion } from 'common/hooks/region';
import { DateTime } from 'luxon';
import { Moment } from 'moment';
import { BackdropLoader } from 'components/Loaders/loader';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSnapshotByTerminal } from 'services/clients/backupBank';
import { fetchTerminals, TerminalQueryKeys } from 'services/clients/terminal';
import { useSegment } from 'common/hooks/segment';
import { TerminalTabs } from '../components/TerminalTabs';
import { TERMINAL_SCREENSHOTS_PARAMS } from '../params';

interface Search {
  terminal: string;
  dateTime: string;
}

export const Preview: React.FC = () => {
  const { searchParams } = useCustomSearchParams(TERMINAL_SCREENSHOTS_PARAMS);
  const region = useRegion();
  const { track } = useSegment();

  // Only create a search query if we have a terminal and date time
  const search: Search | null = React.useMemo(() => {
    if (!searchParams.terminal || !searchParams.date || !searchParams.time) {
      return null;
    }

    const dateTime = DateTime.fromISO(
      `${(searchParams.date as Moment).format('YYYY-MM-DD')}T${searchParams.time}`,
      {
        zone: region.timeZone,
      }
    );

    track(
      'Terminal Screenshots Searched',
      {
        radioButton: 'terminal',
        shippingLine: '',
        containerType: '',
        terminal: searchParams.terminal,
        startDate: searchParams.date.format(),
        startTime: searchParams.time,
        endDate: '',
        endTime: '',
        numberOfDays: 1,
        searchMethod: 'terminal',
      },
      { includeUser: true }
    );

    return { terminal: searchParams.terminal, dateTime: dateTime.toISO() };
  }, [searchParams, region, track]);

  // The query to fetch the snapshot using the search object
  const snapshot = useQuery(
    ['snapshot-by-terminal', search],
    ({ signal }) => getSnapshotByTerminal(search, signal),
    {
      enabled: !!search,
      onError: (e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      },
    }
  );

  const terminals = useQuery(
    [TerminalQueryKeys.Terminals, region.code],
    ({ signal }) => fetchTerminals({ signal, portCollectionCodes: [region.code] }),
    {
      onError: (e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      },
    }
  );

  return (
    <>
      <BackdropLoader open={snapshot.isInitialLoading} />
      {snapshot.isError && (
        <h1 className="text-red-900">There was an error loading the screenshot</h1>
      )}
      {snapshot.data && snapshot.data.url === null ? <h1>No screenshot found</h1> : null}
      {snapshot.data && (
        <TerminalTabs ports={terminals.data || []} snapshotUrls={[snapshot.data]} />
      )}
    </>
  );
};
Preview.displayName = 'ByTerminal.Preview';
